import React from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { FormikProps, withFormik } from 'formik';
import styled from 'styled-components';
import { Button, Form } from 'semantic-ui-react';

import { isProduction } from '@/settings/environment';
import { emailSchema } from '@/services/validators/email';
import { CapyCaptcha, SetCapyValues } from '@/components/shared/CapyCaptcha';
import { Label } from '@/components/shared/Label';

type OuterProps = {
  isLoading: boolean;
  capyError: boolean;
  onSubmit: (values: Values) => void;
  setCapyValues: SetCapyValues;
};

export type Values = {
  email: string;
  ignoreCapyCaptcha: boolean;
};

export const RemindForm = withFormik<OuterProps, Values>({
  mapPropsToValues: () => ({
    email: '',
    ignoreCapyCaptcha: false,
  }),
  validationSchema: emailSchema,
  handleSubmit: (values, { props, setSubmitting }) => {
    props.onSubmit(values);
    setSubmitting(false);
  },
})(
  // enhanced form
  ({
    errors,
    handleChange,
    handleSubmit,
    isLoading = false,
    touched,
    values,
    capyError,
    setCapyValues,
  }: OuterProps & FormikProps<Values>) => {
    const { t } = useTranslation();
    return (
      <Styled>
        <form onSubmit={handleSubmit}>
          <div className="email-input">
            <Form.Input
              data-test="email-field"
              icon="mail"
              iconPosition="left"
              label={t('ui.email.label')}
              className={classnames({ error: errors.email && touched.email })}
              name="email"
              onChange={handleChange}
              placeholder={t('ui.email.placeholder')}
              value={values.email}
            />
            {touched.email && errors.email && (
              <div>
                <Label>{errors.email}</Label>
              </div>
            )}
          </div>
          <div className="capy-wrapper">
            <div>
              <CapyCaptcha onChange={setCapyValues} />
              <div>{t('ui.capyCaptcha.description')}</div>
              {capyError && <Label>{t('ui.capyCaptcha.error')}</Label>}
              {!isProduction() && (
                <Form.Checkbox
                  label={t('ui.capyCaptcha.ignore')}
                  id="ignoreCapyCaptcha"
                  name="ignoreCapyCaptcha"
                  data-test="ignore-capy-captcha"
                  onChange={handleChange}
                />
              )}
            </div>
          </div>
          <div className="submit-button">
            <Button
              data-test="do-verify"
              disabled={isLoading}
              size="large"
              type="submit"
              primary={true}
            >
              {t('ui.action.submit')}
            </Button>
          </div>
        </form>
      </Styled>
    );
  },
);

const Styled = styled.div`
  .email-input {
    margin-bottom: 1.75em;
  }

  .verification-error {
    margin-top: 1.5em;
  }

  .capy-wrapper {
    margin: 0 auto 1.75em;
    text-align: center;
  }
`;
